import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import {
  BlogImageUpload,
  createBlog,
  getBlogByID,
  updateBlog,
} from "./services/blog.services";
import { clearBlogDetails } from "./blogSlice";

import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormRichTextField from "../../components/formFields/FormRichTextField";

import FormTextArea from "../../components/formFields/FormTextArea";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import { slugify } from "../../_helper/helper";

const CreateBlog: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const BlogID = location.state?.BlogID;
  const BlogDetail = useAppSelector((state) => state.blog.BlogDetail);
  const loading = useAppSelector((state) => state.blog.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (BlogID) {
      dispatch(getBlogByID(BlogID));
    }
    return () => {
      dispatch(clearBlogDetails());
    };
  }, [BlogID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [BlogDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ? values.Title : "",
      ShortDescription: values.ShortDescription ? values.ShortDescription : "",
      Keyword: values.Keyword ? values.Keyword : "",
      Description: values.Description ? values.Description : "",
      MetaTitle: values.MetaTitle ? values.MetaTitle : "",
      MetaDescription: values.MetaDescription ? values.MetaDescription : "",
      BlogURL: values.BlogURL ? values.BlogURL : "",
      BlogDate: values.BlogDate
        ? moment(values.BlogDate).format("YYYY-MM-DD")
        : null,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image &&
      typeof values?.Image !== "string" &&
      Array.isArray(values?.Image) &&
      values?.Image?.length > 0
        ? values.Image[0].getRawFile()
        : "";
    const BlogBannerImage =
      values?.BlogBannerImage &&
      typeof values?.BlogBannerImage !== "string" &&
      Array.isArray(values?.BlogBannerImage) &&
      values?.BlogBannerImage?.length > 0
        ? values.BlogBannerImage[0].getRawFile()
        : "";

    // const Image =
    //   values?.Image && typeof values?.Image !== "string"
    //     ? values.Image[0].getRawFile()
    //     : "";
    // const BlogBannerImage =
    //   values?.BlogBannerImage && typeof values?.BlogBannerImage !== "string"
    //     ? values.BlogBannerImage[0].getRawFile()
    //     : "";

    try {
      if (BlogID) {
        const editPayload = {
          ...formData,
          ID: BlogID,
        };

        const response = await dispatch(updateBlog(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values.Image && typeof values.Image !== "string") ||
            (values.BlogBannerImage &&
              typeof values.BlogBannerImage !== "string")
          ) {
            ImageData.append("Image", Image);
            ImageData.append("BlogBannerImage", BlogBannerImage);
            ImageData.append("ID", BlogID);
            await dispatch(BlogImageUpload(ImageData));
          }
          navigate("/blog");
        }
      } else {
        const response = await dispatch(createBlog(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values.Image && typeof values.Image !== "string") ||
              (values.BlogBannerImage &&
                typeof values.BlogBannerImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("BlogBannerImage", BlogBannerImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(BlogImageUpload(ImageData));
          }
          navigate("/blog");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("BlogURL", { value: slug });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={BlogDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {BlogID ? "Update Blog" : "Create Blog"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BlogURL"
                      label="Blog URL"
                      placeholder="Blog URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="BlogDate"
                      label="Blog Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Keyword"
                      label="Keyword"
                      placeholder="Keyword"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2} style={{ width: "100%" }}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormTextArea}
                      astrike={true}
                      style={{ height: 130 }}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    // rowSpan={2}
                    colSpan={2}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      style={{ height: 250 }}
                    />
                  </GridLayoutItem>

                  {/* <GridLayoutItem></GridLayoutItem> */}
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`Image`}
                      label="Blog Image"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Image"
                      component={FormImageUploadMorden}
                    />
                    <Field
                      justifyContent="start"
                      name={`BlogBannerImage`}
                      label="Blog Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Blog Banner Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{
                      marginTop: 40,
                    }}
                  >
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={BlogID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/blog")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateBlog;
